import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ConfirmationSimpleComponent } from '@lib/components/confirmation-simple/confirmation-simple.component';
import { StatusTagComponent } from '@lib/components/status-tag/status-tag.component';
import {
  DerStateTransitionTrigger,
  FileMetadata,
  GedDocumentType,
  QpiStateTransitionTrigger,
  RecueilStateTransitionTrigger,
  SignataireType,
  SignatureState,
  SignerStateStatusEnum,
  SignerStatus,
  UpdateDocumentMetadatasCommandInput,
} from '@lib/models/generated/graphql';
import { DocumentsService } from '@lib/services/documents.service';
import { SignatureService } from '@lib/services/signature.service';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { LoadingService } from '@lib/services/loading.service';
import { SignaturePanelService } from '@lib/components/signature/signature-panel.service';
import { AuthService } from '@lib/services/auth-service.service';
import { getRawFileId } from '@lib/utils/app-utils';

@Component({
  selector: 'app-signataire-details',
  imports: [SHARED_IMPORTS, StatusTagComponent],
  templateUrl: './signataire-details.component.html',
  styleUrl: './signataire-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignataireDetailsComponent {
  public signatureService = inject(SignatureService);
  public signaturePanelService = inject(SignaturePanelService);
  public documentsService = inject(DocumentsService);
  public dialogService = inject(OuiDialogService);
  public snackbarService = inject(OuiSnackbarService);
  public loadingService = inject(LoadingService);
  public investisseurService = inject(InvestisseurService);
  private authService = inject(AuthService);
  public signatureStateEnum = SignatureState;
  public signerStatusEnum = SignerStatus;
  public signataireTypeEnum = SignataireType;
  public files = this.signaturePanelService.files;
  public isReadOnly = computed(() => this.authService.isBackOffice());
  public firstFile = computed<FileMetadata>(() => this.files()[0]);
  public canCancelSignature = computed(() => !!this.firstFile().signatureStates?.states?.length);
  public fileUrls = computed<string[]>(() => this.files().map(f => f.url ?? ''));
  public filenames = computed<string[]>(() => this.files().map(f => f.fileName ?? ''));
  public isMultipleFiles = computed<boolean>(() => this.files().length > 1);
  public relanceSignerStatus = [SignerStatus.CodeSent, SignerStatus.Ready, SignerStatus.Accessed, SignerStatus.PendingValidation];
  public signatureRequestStatus = SignerStateStatusEnum;

  public getTotalSigned(): number {
    return this.firstFile()?.signatureStates?.states?.filter(s => s.signatureState === 'SIGNED')?.length ?? 0;
  }

  public getSignaturePercent(): number {
    return (this.getTotalSigned() / (this.firstFile()?.signatureStates?.states?.length ?? 1)) * 100;
  }

  public cancelSignature(): void {
    const dialogRef = this.dialogService.openDialog(ConfirmationSimpleComponent, {
      title: 'Annuler la signature',
      message:
        'En cliquant sur "Valider", cela mettra fin à la session de signature pour tous les destinataires. Cette action est irréversible.',
      validateButtonLabel: 'Valider',
    });

    dialogRef.afterClosed().subscribe(async cancel => {
      if (cancel) {
        this.signaturePanelService.closePanel();
        this.loadingService.startLoading();
        try {
          const fileId = getRawFileId(this.firstFile()?.fileId as string);
          // Cancel signature
          await this.signatureService.cancelSignatures([fileId]);

          // update signature state to have the new status directly
          const newMetadata = {} as UpdateDocumentMetadatasCommandInput;
          newMetadata.signatureState = SignatureState.Cancelled;

          const results = await Promise.all(
            this.files().map(async file => {
              return await this.documentsService.updateFileMetadata(getRawFileId(file.fileId as string), newMetadata);
            })
          );
          if (results.some(result => !result)) {
            throw new Error('Failed to update signature metadata');
          }

          const investisseurIds =
            this.firstFile()
              ?.signatureStates?.states?.filter(s => s.type === SignataireType.Investisseur)
              .map(s => s.internalId as number) ?? [];

          // Update investisseur metadata state if concerned
          const requests: Promise<void>[] = [];
          this.files().forEach(file => {
            const typeDoc = file.typeDocumentCode;
            const currentFileId = getRawFileId(file.fileId as string);
            if (typeDoc === GedDocumentType.Der) {
              investisseurIds.map(id =>
                requests.push(
                  this.investisseurService.updateDERState(
                    DerStateTransitionTrigger.DerSignatureIsCancelled,
                    false,
                    currentFileId,
                    id,
                    undefined
                  )
                )
              );
            }

            if (typeDoc === GedDocumentType.RecInfo) {
              investisseurIds.map(id =>
                requests.push(
                  this.investisseurService.updateRecueilState(
                    RecueilStateTransitionTrigger.RecueilSignatureIsCancelled,
                    currentFileId,
                    id,
                    undefined,
                    undefined
                  )
                )
              );
            }

            if (typeDoc === GedDocumentType.ProfInvest) {
              investisseurIds.map(id =>
                requests.push(
                  this.investisseurService.updateQPIState(QpiStateTransitionTrigger.QpiSignatureIsCancelled, currentFileId, id, undefined)
                )
              );
            }
          });

          if (requests.length > 0) {
            await Promise.all(requests);
          }

          await this.investisseurService.getInvestisseurDocuments();
          this.signatureService.triggerRefreshData();
          this.snackbarService.open('La signature du document a été annulée', 'success');
        } catch (err) {
          console.error(err);
          this.snackbarService.open(
            "Une erreur est survenue lors de l'annulation des signatures. Veuillez rafraîchir la page et contacter l'administrateur si l'erreur persiste.",
            'error'
          );
        } finally {
          this.loadingService.stopLoading();
        }
      }
    });
  }

  public relanceSignature(): void {
    const dialogRef = this.dialogService.openDialog(ConfirmationSimpleComponent, {
      title: 'Relancer le signataire',
      message: 'Cette action renverra un e-mail au signataire en cours. Voulez-vous continuer ?',
      validateButtonLabel: 'Relancer',
    });

    dialogRef.afterClosed().subscribe(async relance => {
      if (relance) {
        this.signaturePanelService.closePanel();
        this.loadingService.startLoading();
        try {
          const fileId = getRawFileId(this.firstFile()?.fileId as string);
          const result = await this.signatureService.relanceSignatures([fileId]);
          if (result) {
            this.snackbarService.open('Un e-mail de relance a été envoyé', 'success');
          }
        } catch (error) {
          console.error(error);
          this.snackbarService.open(
            "Une erreur est survenue lors de la relance des signatures.  Veuillez rafraîchir la page et contacter l'administrateur si l'erreur persiste.",
            'error'
          );
        } finally {
          this.loadingService.stopLoading();
        }
      }
    });
  }

  public async downloadDocument(): Promise<void> {
    if (this.isMultipleFiles()) {
      await this.documentsService.downloadDocumentsAsZip(
        this.files().map(f => ({ url: f.url as string, name: f.fileName as string })),
        'documents-signature'
      );
    } else {
      await this.documentsService.downloadDocument({
        url: this.firstFile()?.url as string,
        name: this.firstFile()?.fileName as string,
      });
    }
  }
}
